import Icon from "../../../components/base/Icon";
import { CatalogRow } from "../models/PlanCatalog";
import { offerText, toCount, toMoney, toRate } from "./format";


// One plan: its price, what it includes, what it charges past that, and how many customers
// are on it -- with how many of them still hold older terms.
export default function PlanCatalogCard(props: {
    row: CatalogRow,
    onEdit: () => void,
    onApply: () => void
}) {

    const r = props.row
    const p = r.plan

    const channel = (label: string, on?: boolean) =>
        <span className={"catalog-chip" + (on ? " is-on" : "")}>
            <Icon icon={on ? "check" : "minus"} type="solid" size={9} />
            {label}
        </span>

    const addon = (label: string, included?: boolean, price?: number) =>
        <span className={"catalog-chip" + (included ? " is-on" : (price ?? 0) > 0 ? " is-sold" : "")}>
            <Icon icon={included ? "check" : (price ?? 0) > 0 ? "dollar-sign" : "minus"} type="solid" size={9} />
            {label}
            <em>{offerText(included, price)}</em>
        </span>

    return (
        <div className={"catalog-card" + (p.isActive ? "" : " is-off")}>

            <div className="catalog-card-top">
                <div className="catalog-card-eyebrow">{p.eyebrow || " "}</div>
                <div className="catalog-card-name">
                    {p.name}
                    <span className="catalog-card-code">{p.code}</span>
                </div>
                <div className="catalog-card-badges">
                    {!p.isActive && <span className="badge badge-light-danger">Not on sale</span>}
                    {p.highlighted && <span className="badge badge-light-primary">Best value</span>}
                    {p.darkCard && <span className="badge badge-light-dark">Dark card</span>}
                </div>

                <div className="catalog-card-price">
                    {
                        p.isCustomPriced ?
                            <span>{p.priceText || "Custom"}</span>
                            :
                            <>
                                <span>{toMoney(p.monthlyPrice)}</span><small>/month</small>
                            </>
                    }
                </div>
                <div className="catalog-card-annual">
                    {
                        p.isCustomPriced ? "Quoted per customer"
                            : p.annualPrice > 0 ? `${toMoney(p.annualPrice)} a year`
                                : "Not sold yearly"
                    }
                </div>
            </div>

            <div className="catalog-card-section">
                <div className="catalog-card-label">Included</div>
                <div className="catalog-card-figures">
                    <div><b>{toCount(p.includedUsers)}</b><span>users at once</span></div>
                    <div><b>{toCount(p.includedNumbers)}</b><span>phone numbers</span></div>
                    <div><b>{toCount(p.includedMessages)}</b><span>messages / mo</span></div>
                    <div><b>{toCount(p.includedMinutes)}</b><span>minutes / mo</span></div>
                </div>
            </div>

            <div className="catalog-card-section">
                <div className="catalog-card-label">Beyond the allowance</div>
                <div className="catalog-card-rates">
                    <span>{toRate(p.overageMessageRate)} <em>/ message</em></span>
                    <span>{toRate(p.overageMinuteRate)} <em>/ minute</em></span>
                    <span>{toMoney(p.extraNumberPrice)} <em>/ extra number</em></span>
                </div>
            </div>

            <div className="catalog-card-section">
                <div className="catalog-card-label">Channels &amp; add-ons</div>
                <div className="catalog-card-chips">
                    {channel("WhatsApp", p.whatsAppIncluded)}
                    {channel("Telegram", p.telegramIncluded)}
                    {addon("Masked Job Calls", p.extensionsSupportIncluded, p.extensionsSupportPrice)}
                </div>
                <div className="catalog-card-small">
                    Recordings kept {p.recordingRetentionMonths > 0 ? `${p.recordingRetentionMonths} month${p.recordingRetentionMonths === 1 ? "" : "s"}` : "— not kept"}
                    {p.supportTier && <> · Support: {p.supportTier}</>}
                </div>
            </div>

            <div className="catalog-card-foot">
                <div className="catalog-card-subs">
                    <div>
                        <Icon icon="users" size={12} />
                        <b>{r.subscribers}</b> subscriber{r.subscribers === 1 ? "" : "s"}
                    </div>
                    {
                        r.outdated > 0 &&
                        <div className="text-warning">
                            <Icon icon="clock-rotate-left" size={12} />
                            <b>{r.outdated}</b> on older terms
                        </div>
                    }
                    {
                        r.scheduled > 0 &&
                        <div className="text-primary">
                            <Icon icon="calendar-check" size={12} />
                            <b>{r.scheduled}</b> change{r.scheduled === 1 ? "" : "s"} scheduled
                        </div>
                    }
                </div>
                <div className="catalog-card-actions">
                    <button type="button" className="btn btn-sm btn-light-primary flex-grow-1"
                        onClick={e => { e.preventDefault(); props.onEdit(); }}>
                        <Icon icon="pen" type="solid" size={11} className="me-2" />
                        Edit
                    </button>
                    {
                        !p.isCustomPriced &&
                        <button type="button" className="btn btn-sm btn-light-warning flex-grow-1"
                            disabled={r.outdated === 0}
                            title={r.outdated === 0 ? "Every subscriber already has what this plan says" : "Move existing subscribers onto these terms at their renewal"}
                            onClick={e => { e.preventDefault(); props.onApply(); }}>
                            <Icon icon="paper-plane" type="solid" size={11} className="me-2" />
                            Send to subscribers
                        </button>
                    }
                </div>
            </div>

        </div>
    )
}
